import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetRecentCallsQuery, useGetVoicemailsByExtensionQuery } from '../services/VoipService';
import { getLoggedInUser, getLoggedInUserOrganization } from '../selectors/userSelectors';
import useLastRecentsViewedAt from './useLastRecentsViewedAt';

/**
 * Centralised badge counts for the RhinoVOIP notification feature.
 *
 * Returns:
 *   newMissedCount  — inbound missed/no_answer calls whose start time is after
 *                     the last time the member opened the Recents tab.
 *   unreadVmCount   — voicemails with message_status NULL/'unread'.
 *   totalNewCount   — sum of the two, used by the nav badge.
 *
 * Queries are skipped (no network traffic) for members who don't have a VoIP
 * extension or whose org has VoIP disabled — same gating used elsewhere.
 */
export default function useVoipBadgeCounts() {
  const currentUser = useSelector(getLoggedInUser);
  const organization = useSelector(getLoggedInUserOrganization);
  const hasExtension = Array.isArray(currentUser?.extensions) && currentUser.extensions.length > 0;
  const enabled = !!organization?.isVoipEnabled && hasExtension;

  const { lastViewedAtMs } = useLastRecentsViewedAt();

  const { data: recentCallsData } = useGetRecentCallsQuery(true, { skip: !enabled });
  const { data: voicemailsData } = useGetVoicemailsByExtensionQuery(true, { skip: !enabled });

  const newMissedCount = useMemo(() => {
    if (!enabled) return 0;
    const records = Array.isArray(recentCallsData?.objects) ? recentCallsData.objects : [];
    return records.reduce((count, r) => {
      const statusLower = (r.status || '').toLowerCase();
      const isMissed = statusLower === 'missed' || statusLower === 'no_answer';
      if (!isMissed) return count;
      if ((r.direction || '').toLowerCase() !== 'inbound') return count;
      const ts = r.start_stamp ? new Date(r.start_stamp).getTime() : 0;
      if (!ts || ts <= lastViewedAtMs) return count;
      return count + 1;
    }, 0);
  }, [enabled, recentCallsData, lastViewedAtMs]);

  const unreadVmCount = useMemo(() => {
    if (!enabled) return 0;
    const records = Array.isArray(voicemailsData?.objects) ? voicemailsData.objects : [];
    return records.reduce((count, vm) => {
      const status = vm.message_status;
      const isUnread = status == null || status === '' || status === 'unread';
      return isUnread ? count + 1 : count;
    }, 0);
  }, [enabled, voicemailsData]);

  return {
    newMissedCount,
    unreadVmCount,
    totalNewCount: newMissedCount + unreadVmCount,
  };
}
